/**
 * @testrelic/playwright-analytics/visual
 *
 * Registers `toMatchVisualBaseline` onto Playwright's `expect` and declares it
 * to TypeScript.
 *
 * Importing this module is only necessary when using Playwright's own `expect`.
 * The SDK fixture (`@testrelic/playwright-analytics/fixture`) already imports
 * it, so `expect` from there has the matcher without a second import.
 *
 * Native `toHaveScreenshot()` needs none of this — the reporter recovers those
 * comparisons from the attachments on its own.
 */

import { expect as playwrightExpect } from '@playwright/test';
import { toMatchVisualBaseline } from './visual-matcher.js';

export type { VisualBaselineOptions } from './visual-matcher.js';
export { toMatchVisualBaseline } from './visual-matcher.js';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace PlaywrightTest {
    // `T` is unused here but structurally required: declaration merging only
    // works against Playwright's `Matchers<R, T = unknown>` if the parameter
    // list matches, and dropping it silently stops the merge.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface Matchers<R, T = unknown> {
      /**
       * Compare a page or locator against its committed visual baseline.
       *
       * Uses Playwright's own comparator, so every `toHaveScreenshot` option
       * (`threshold`, `maxDiffPixels`, `maxDiffPixelRatio`, `mask`, `clip`,
       * `fullPage`, `animations`, …) applies unchanged. Baselines live where
       * Playwright puts them and are updated with `--update-snapshots`.
       *
       * ```ts
       * await expect(page).toMatchVisualBaseline('home', {
       *   maxDiffPixelRatio: 0.01,
       *   mask: [page.locator('.live-ticker')],
       * });
       * ```
       */
      toMatchVisualBaseline(
        name: string,
        options?: import('./visual-matcher.js').VisualBaselineOptions,
      ): Promise<R>;
    }
  }
}

// Registered at module scope so a bare import is enough to install it.
// `expect.extend` is additive and idempotent here: the fixture and a direct
// import of this module both land on the same registration.
playwrightExpect.extend({ toMatchVisualBaseline });
